// Documents on an intake request or a quote (prototype docUI). The API enforces every rule;
// the checks here only save a round trip and explain the limits up front.
import { useRef, useState } from 'react';
import { Banner, useApiClient, useDownload, useQuery, type Fetcher } from '@acc/web-shared';
import { dateTime, useAgency } from './agency';

export const ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx';
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_FILES = 10;

export interface DocumentItem {
  documentId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedAt: string;
  canDelete: boolean;
}

interface RemovedItem { documentId: string; fileName: string; sizeBytes: number; deletedBy: string | null; deletedAt: string }

export const fileSize = (n: number) => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

/** Problems with the chosen files, before anything is sent. */
export function checkFiles(files: File[], existing = 0): string | null {
  const ext = new Set(ACCEPT.split(','));
  const bad = files.find((f) => !ext.has(`.${f.name.split('.').pop()?.toLowerCase()}`));
  if (bad) return `${bad.name}: only PDF, PNG, JPG and Word files can be attached.`;
  const big = files.find((f) => f.size > MAX_BYTES);
  if (big) return `${big.name} is larger than 5 MB.`;
  if (existing + files.length > MAX_FILES) return `A record can hold at most ${MAX_FILES} documents.`;
  return null;
}

/** Uploads one at a time; returns the names that failed with their reasons. */
export async function uploadAll(api: Fetcher, path: string, files: File[]): Promise<string[]> {
  const failed: string[] = [];
  for (const file of files) {
    try { await api(path, { method: 'POST', file }); } catch (err) { failed.push(`${file.name}: ${(err as Error).message}`); }
  }
  return failed;
}

export function DocumentsPanel({ path, onChange, readOnlyNote }: { path: string; onChange?: () => void; readOnlyNote?: string }) {
  const a = useAgency();
  const api = useApiClient();
  const download = useDownload();
  const q = useQuery<{ documents: DocumentItem[]; canAdd: boolean; unavailable?: boolean; removed?: RemovedItem[] }>(a.path(path));
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const docs = q.data?.documents ?? [];

  async function add(list: FileList | null) {
    const files = [...(list ?? [])];
    if (input.current) input.current.value = '';
    if (!files.length) return;
    const problem = checkFiles(files, docs.length);
    if (problem) { setError(problem); return; }
    setBusy(true); setError('');
    const failed = await uploadAll(api, a.path(path), files);
    setBusy(false);
    if (failed.length) setError(`Not attached — ${failed.join('; ')}`);
    else a.notify('ok', `${files.length} document${files.length > 1 ? 's' : ''} attached.`);
    q.reload(); onChange?.();
  }

  async function remove(d: DocumentItem) {
    if (!window.confirm(`Remove ${d.fileName}? The agency owner or office manager can restore it.`)) return;
    setBusy(true); setError('');
    try {
      await api(a.path(`/documents/${d.documentId}`), { method: 'DELETE' });
      a.notify('ok', `${d.fileName} removed.`);
      q.reload(); onChange?.();
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  async function restore(d: RemovedItem) {
    setBusy(true); setError('');
    try {
      await api(a.path(`/documents/${d.documentId}/restore`), { method: 'POST' });
      a.notify('ok', `${d.fileName} restored.`);
      q.reload(); onChange?.();
    } catch (err) { setError((err as Error).message); } finally { setBusy(false); }
  }

  async function open(d: DocumentItem) {
    setError('');
    try { await download(a.path(`/documents/${d.documentId}/content`), d.fileName); } catch (err) { setError((err as Error).message); }
  }

  return (
    <section className="card" aria-labelledby="documents">
      <h2 id="documents">Documents</h2>
      {q.error && <Banner tone="bad">{q.error.message}</Banner>}
      {error && <Banner tone="bad">{error}</Banner>}
      {q.loading && !q.data && <p className="muted">Loading…</p>}
      {q.data?.unavailable && <p className="muted">Document storage is being set up. Attachments will be available here soon.</p>}
      {q.data && !q.data.unavailable && !docs.length && <p className="muted">No documents attached yet.</p>}
      {docs.length > 0 && (
        <ul className="doc-list">
          {docs.map((d) => (
            <li key={d.documentId}>
              <button type="button" className="linkish" onClick={() => void open(d)} aria-label={`Download ${d.fileName}`}>{d.fileName}</button>
              <span className="muted"> · {fileSize(d.sizeBytes)} · {a.name(d.uploadedBy)} · {dateTime(d.uploadedAt)}</span>
              {d.canDelete && <button type="button" className="btn small" disabled={busy} onClick={() => void remove(d)} aria-label={`Remove ${d.fileName}`}>Remove</button>}
            </li>
          ))}
        </ul>
      )}
      {q.data?.canAdd && (
        <label className="field" style={{ marginTop: 12 }}>Add files (PDF, images, Word · 5 MB each · up to {MAX_FILES})
          <input ref={input} type="file" multiple accept={ACCEPT} disabled={busy} onChange={(e) => void add(e.target.files)} />
        </label>
      )}
      {q.data?.removed && q.data.removed.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary>Removed documents ({q.data.removed.length})</summary>
          <ul className="doc-list">
            {q.data.removed.map((d) => (
              <li key={d.documentId}>
                <span>{d.fileName}</span>
                <span className="muted"> · {fileSize(d.sizeBytes)} · removed by {a.name(d.deletedBy)} · {dateTime(d.deletedAt)}</span>
                <button type="button" className="btn small" disabled={busy} onClick={() => void restore(d)} aria-label={`Restore ${d.fileName}`}>Restore</button>
              </li>
            ))}
          </ul>
        </details>
      )}
      {busy && <p className="muted" role="status">Working…</p>}
      {q.data && !q.data.canAdd && readOnlyNote && <p className="muted" style={{ fontSize: 13 }}>{readOnlyNote}</p>}
      <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>Fictional documents only until Farm Bureau approves real customer information.</p>
    </section>
  );
}
